import { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { fetchApps } from "@/lib/api";
import { AppCard } from "@/components/AppCard";
import { Skeleton } from "@/components/ui/skeleton";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const AppGrid = ({ onAppsLoaded }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchApps();
        if (!cancelled) {
          setApps(data);
          onAppsLoaded?.(data);
        }
      } catch (e) {
        if (!cancelled) setError("No se pudieron cargar las apps. Reintentá.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onAppsLoaded]);

  const categories = useMemo(() => {
    const set = new Set(apps.map((a) => a.category));
    return ["all", ...Array.from(set)];
  }, [apps]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter((a) => {
      const matchCat = activeCat === "all" || a.category === activeCat;
      if (!matchCat) return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        a.tagline.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q)
      );
    });
  }, [apps, query, activeCat]);

  const handleClicked = (slug, clicks) => {
    setApps((prev) =>
      prev.map((a) => (a.slug === slug ? { ...a, clicks } : a))
    );
  };

  return (
    <section
      id="apps"
      data-testid="apps-section"
      className="relative bg-grain border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent-green))] mb-3">
              // section_02
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-none">
              Mis aplicaciones
            </h2>
            <p className="text-muted-foreground mt-4 max-w-xl">
              Cada card abre la app en una nueva pestaña. Hacé clic en la que te interese y
              probala.
            </p>
          </div>

          {/* Search */}
          <div className="relative w-full md:w-80">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <input
              data-testid="search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="buscar app..."
              className="w-full bg-transparent border border-border focus:border-[hsl(var(--accent-cyan))] outline-none px-9 py-2.5 font-mono text-sm placeholder:text-muted-foreground/60 transition-colors"
            />
            {query && (
              <button
                data-testid="search-clear"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:text-foreground text-muted-foreground"
                aria-label="Limpiar búsqueda"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Category filters */}
        <div
          data-testid="filters"
          className="flex flex-wrap items-center gap-2 mb-10"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mr-2">
            categorías:
          </span>
          {categories.map((cat) => {
            const id = cat === "all" ? "all" : slugify(cat);
            const active = activeCat === cat;
            return (
              <button
                key={cat}
                data-testid={`filter-${id}`}
                onClick={() => setActiveCat(cat)}
                className={[
                  "font-mono text-xs uppercase tracking-[0.15em] px-3 py-1.5 border transition-all",
                  active
                    ? "bg-foreground text-background border-foreground"
                    : "border-border hover:border-foreground text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                {cat === "all" ? "todas" : cat}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {loading && (
          <div
            data-testid="apps-loading"
            className="grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        )}

        {!loading && error && (
          <div
            data-testid="apps-error"
            className="border border-destructive/50 bg-destructive/10 text-destructive p-6 font-mono text-sm"
          >
            {error}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div
            data-testid="apps-empty"
            className="border border-border p-10 text-center"
          >
            <p className="font-mono text-sm text-muted-foreground">
              // no se encontraron apps con esos criterios
            </p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div
            data-testid="apps-grid"
            className="grid grid-cols-1 md:grid-cols-2 gap-5 stagger-fade"
          >
            {filtered.map((app) => (
              <AppCard
                key={app.slug}
                app={app}
                onClicked={handleClicked}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AppGrid;
